import { Component, OnInit, ViewChild, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { SiteDto } from '@app/admin/models/site-dto';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { AddLocationComponent } from './add-location/add-location.component';
import { Store } from '@ngrx/store';
import { SiteState } from '@app/core/store/reducers/site';
import { LoadSites } from '@app/core/store/actions/site.action';
import { getSites } from '@app/core/store/selectors/site.selector';
import { takeUntil } from 'rxjs/operators';
import { getResourceState } from '@dwo/selectors';
import { ResourceStatus, ResourceType } from '@dwo/enums';

@Component({
  selector: 'app-locations',
  templateUrl: './locations.component.html',
  styleUrls: ['./locations.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LocationsComponent implements OnInit, OnDestroy {
  displayColumns: string[] = ['id', 'name', 'plantCode', 'actions'];
  dataSource: MatTableDataSource<SiteDto> = new MatTableDataSource();
  filter = '';
  loading = false;
  loadingDelayed = false;
  private readonly destroy$ = new Subject<void>();
  private readonly loadingFallbackMs = 10000;
  private loadingFallbackTimer?: ReturnType<typeof setTimeout>;

  @ViewChild(MatSort, { static: false }) set matSort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  // @Select(SitesState.getSites) sites$: Observable<SiteDto[]>;
  // @Select(SitesState.hasSites) hasSites$: Observable<boolean>;
  // @Select(SitesState.loading) loading$: Observable<boolean>;
  // @Select(SitesState.filter) filter$: Observable<string>;

  constructor(
    private store: Store<SiteState>,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(new LoadSites());
    this.store
      .select(getSites)
      .pipe(takeUntil(this.destroy$))
      .subscribe((sites) => {
        this.dataSource.data = sites;
      });
    this.store
      .select(getResourceState, ResourceType.LoadSites)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        const isLoading = state.status === ResourceStatus.InProgress;
        this.loading = isLoading;

        if (isLoading) {
          this.startLoadingFallback();
          return;
        }

        this.loadingDelayed = false;
        this.clearLoadingFallback();
      });

    // this.subscriptions.add(
    //   this.sites$.subscribe((sites) => {
    //     this.dataSource.data = sites;
    //   }),
    // );
  }

  ngOnDestroy(): void {
    this.clearLoadingFallback();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onRefresh(): void {
    this.store.dispatch(new LoadSites());
    this.startLoadingFallback();
  }

  onFilter(filter: string): void {
    this.filter = filter;
    this.dataSource.filter = filter.trim().toLowerCase();
  }

  onAdd() {
    this.dialog
      .open(AddLocationComponent)
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.store.dispatch(new LoadSites());
          this.startLoadingFallback();
        }
      });
  }

  private startLoadingFallback(): void {
    this.clearLoadingFallback();
    this.loadingDelayed = false;
    this.loadingFallbackTimer = setTimeout(() => {
      this.loading = false;
      this.loadingDelayed = true;
      this.loadingFallbackTimer = undefined;
    }, this.loadingFallbackMs);
  }

  private clearLoadingFallback(): void {
    if (!this.loadingFallbackTimer) {
      return;
    }

    clearTimeout(this.loadingFallbackTimer);
    this.loadingFallbackTimer = undefined;
  }
}
